import clsx from 'clsx';
import styles from './styles.module.scss';
import { useOrientation } from '@/hooks';
import { useConfigStore, useShopStore } from '@/stores';
import { useTranslation } from 'react-i18next';
import { useShopBuildingOptions, useShopFloorList } from '@/hooks/poi/useShopData';
import { SHOP_SEARCH_MODE } from '@/enums/shop';
import type { FloorInfo } from '@/services/dto/kiosk-config';
import ShopFnbFacilityFloorButton from '@/components/common/shop-fnb-facility/buttons/floor';
import ShopFnbFacilityBuildingDropdown from '@/components/common/shop-fnb-facility/building-dropdown';
import { AppUtils } from '@/utils/app';

const ShopSearchByFloor = () => {
    const { t } = useTranslation();
    const { isHorizontal } = useOrientation();
    const selectedFloor = useShopStore((state) => state.selectedFloor);
    const selectedBuildingId = useShopStore((state) => state.selectedBuildingId);
    const setSelectedFloor = useShopStore((state) => state.setSelectedFloor);
    const setSelectedBuildingId = useShopStore((state) => state.setSelectedBuildingId);
    const updateShopContents = useShopStore((state) => state.updateShopContents);
    const screenMode = useConfigStore((s) => s.config?.ui?.screenMode);
    const voice = useConfigStore((state) => state.config.tts.voice);

    const buildingOptions = useShopBuildingOptions(t);
    const floorList = useShopFloorList(selectedBuildingId, t);

    const handleSelectFloor = (floor: { id: string; label: string; value: string; data: FloorInfo | null | string[] }) => {
        setSelectedFloor(floor as any);
        updateShopContents(SHOP_SEARCH_MODE.SEARCH_BY_FLOOR);
    };

    const handleSelectBuilding = (buildingId: string) => {
        const allFloorOption = {
            id: 'all',
            label: t('shop.search.text.content.all_floor'),
            value: 'all',
            data: null,
        };
        setSelectedBuildingId(buildingId);
        setSelectedFloor(allFloorOption as Parameters<typeof setSelectedFloor>[0]);
        updateShopContents(SHOP_SEARCH_MODE.SEARCH_BY_FLOOR);

        if (voice) {
            setTimeout(() => {
                const firstFloorElement = document.getElementById('shop-first-floor');
                if (firstFloorElement) {
                    firstFloorElement.blur();
                    firstFloorElement.focus();
                }
            }, 500);
        }
    };

    return (
        <div
            className={clsx(styles['search-by-floor'], {
                [styles['search-by-floor--vertical']]: !isHorizontal,
                [styles['search-by-floor--vertical__halfmode']]: !isHorizontal && screenMode === 'split',
                ['ml-0! justify-center!']: buildingOptions.length === 1,
            })}
        >
            {buildingOptions.length > 1 && (
                <div
                    className={clsx(styles['search-by-floor__dropdown'], {
                        [styles['search-by-floor__dropdown--vertical-halfmode']]: !isHorizontal && screenMode === 'split',
                    })}
                >
                    <ShopFnbFacilityBuildingDropdown defaultValue={selectedBuildingId || 'all'} options={buildingOptions} onSelect={handleSelectBuilding} />
                </div>
            )}
            <div
                className={clsx(
                    styles['search-by-floor__floors'],
                    {
                        [styles['search-by-floor__floors--vertical']]: !isHorizontal,
                        [styles['search-by-floor__floors--vertical-halfmode']]: !isHorizontal && screenMode === 'split',
                        ['justify-center']: buildingOptions.length === 1 || (!isHorizontal && screenMode === 'split'),
                    },
                    'scrollbar-custom-gray-99938f scrollbar-w-4 overflow-y-auto'
                )}
                data-region-nav
            >
                {floorList.map((floor, index) => {
                    return (
                        <ShopFnbFacilityFloorButton
                            id={index === 0 ? 'shop-first-floor' : undefined}
                            tabIndex={9}
                            data-interactive
                            role="radio"
                            type="button"
                            key={floor.id}
                            classes={floor.id === 'all' ? 'text' : 'number'}
                            aria-label={
                                floor.id === 'all'
                                    ? t('shop.search.tts.content.all_floors')
                                    : t('shop.search.tts.content.floor', { floor: AppUtils.getFloorTTSText(floor.label) })
                            }
                            title={floor.label}
                            aria-checked={floor.id === selectedFloor?.id}
                            onClick={() => handleSelectFloor(floor)}
                            isVertical={!isHorizontal}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default ShopSearchByFloor;
